import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { DownloadSimpleIcon } from '@phosphor-icons/react/dist/csr/DownloadSimple';

import { AuditEntryTable } from './AuditEntryTable';
import styles from './AuditLogScreen.module.css';
import { PageActions } from '../../app/shell/PageActions';
import { FilterBar } from '../../design-system/composites/FilterBar/FilterBar';
import { Button } from '../../design-system/primitives/Button/Button';
import { ButtonLink } from '../../design-system/primitives/Button/ButtonLink';
import { Input } from '../../design-system/primitives/Input/Input';
import type { ListStatus } from '../../design-system/composites/DataTable/DataTable';
import { Pagination } from '../../design-system/composites/Pagination/Pagination';
import { useCursorPages } from '../../design-system/composites/Pagination/useCursorPages';
import { ApiError, api, type AuditEntry, type AuditQuery } from '../../lib/api/client';

/**
 * The administrator's full audit trail — `GET /audit-log`, behind `audit.view`
 * (gated in `App.tsx`, not here: this component assumes it is only ever
 * mounted for a session that already holds the permission).
 *
 * SPLIT FROM `AuditEntryTable` ON PURPOSE. This screen owns everything that
 * changes the REQUEST: the filters, the cursor, the export link. It holds no
 * opinion about how a row renders — that is `AuditEntryTable`'s job alone,
 * and task 8 reuses it unchanged for the profile "my activity" screen.
 *
 * ## The cursor is opaque, and treated that way throughout
 *
 * `meta.next_cursor` (`client.ts`'s own docblock) encodes a SIGNED
 * `(occurred_at, id)` position server-side. Nothing here parses it, inspects
 * it, or builds one from scratch — it is read out of one response and handed
 * back verbatim as `cursor` on the next `api.auditLog` call, and NOWHERE
 * ELSE: it lives in `useState`, never in the URL. This SPA has no router
 * (`App.tsx`'s own docblock), and even if it had one, a cursor is a signed
 * token with a server-checked expiry — not a shareable view parameter the way
 * `sort` is on `DataTable`.
 *
 * `cursor` and `appliedFilters` are deliberately TWO pieces of state rather
 * than one. A new filter submission resets `cursor` to `undefined` in the
 * same handler that changes `appliedFilters` — React 18 batches both into one
 * commit, so `load` only ever runs once for a filter change, not once for
 * each state write. Paging forward changes `cursor` alone, leaving
 * `appliedFilters` untouched, which is what lets "Next page" ask for the next
 * slice of the SAME filtered view rather than silently dropping the filters
 * an administrator just set.
 *
 * ## `draftFilters` vs. `appliedFilters`
 *
 * The five inputs are controlled against `draftFilters`, typed freely with no
 * request fired on every keystroke — `usersApi`-style screens don't have this
 * problem because they have no filters at all. A request fires only from
 * `handleFilterSubmit` (the form's own submit, not a debounce) or
 * `handleClearFilters`, both of which copy `draftFilters` into
 * `appliedFilters` — the value the `useEffect` below actually depends on and
 * the value `auditLogExportUrl` is built from, so exporting always reflects
 * the filters an administrator has actually applied, not ones half-typed
 * into a field.
 *
 * ## Decision V-7 — the notice this screen carries and never earns
 *
 * `entry_hash`/`previous_hash` make the trail tamper-EVIDENT, not
 * tamper-PROOF: verifying the chain is checking every row's hash against its
 * predecessor's, which is O(rows) on a table this screen's own filters prove
 * only grows. Rendering a "chain verified" badge here would mean, at best,
 * "we rendered some rows" — this screen says plainly that it does not
 * perform that check, rather than implying a guarantee `AuditLogScreen`
 * cannot make good on. See `audit.notVerifiedNotice` below.
 */

interface FilterState {
  actor: string;
  action: string;
  entityType: string;
  from: string;
  to: string;
}

const BLANK_FILTERS: FilterState = {
  actor: '',
  action: '',
  entityType: '',
  from: '',
  to: '',
};

/**
 * Every text filter is a SEARCH on the server, not an exact value: `actor`
 * matches the label (`system`), username or full name; `action` is a prefix
 * (`user.` finds every `user.*` row); `entity_type` is a substring (`user`
 * finds the User rows). Blank fields are dropped rather than sent.
 */
function toQuery(filters: FilterState, cursor?: string, perPage?: number): AuditQuery {
  const query: AuditQuery = {};

  if (filters.actor.trim() !== '') query.actor = filters.actor.trim();
  if (filters.action.trim() !== '') query.action = filters.action.trim();
  if (filters.entityType.trim() !== '') query.entity_type = filters.entityType.trim();
  if (filters.from.trim() !== '') query.from = filters.from.trim();
  if (filters.to.trim() !== '') query.to = filters.to.trim();
  // Opaque and handed back exactly as `meta.next_cursor` supplied it — see
  // this file's own docblock and client.ts's.
  if (cursor !== undefined) query.cursor = cursor;
  if (perPage !== undefined) query.per_page = perPage;

  return query;
}

/**
 * Whether `query` carries any FILTER — `cursor` does not count, a page-two
 * request under no filter is still "no filter applied". Decides `'empty'`
 * (nothing has ever happened) versus `'no-results'` (something has, these
 * filters hide it) when a page comes back with zero rows — see I-3 in the
 * review this fixes.
 */
function hasAppliedFilters(query: AuditQuery): boolean {
  return (
    query.actor_user_id !== undefined ||
    query.actor !== undefined ||
    query.action !== undefined ||
    query.entity_type !== undefined ||
    query.from !== undefined ||
    query.to !== undefined
  );
}

export function AuditLogScreen() {
  const { t } = useTranslation();

  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly AuditEntry[]>([]);
  const [reference, setReference] = useState<string | null>(null);
  // 50 — `AuditLogIndexRequest::DEFAULT_PER_PAGE` server-side.
  const pages = useCursorPages();
  // Destructured — `pages` is a fresh object every render; see
  // `CaseList.tsx`'s identical note.
  const { observe } = pages;
  const [draftFilters, setDraftFilters] = useState<FilterState>(BLANK_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(BLANK_FILTERS);

  /**
   * C-2's fix. A generation counter, not a boolean `cancelled` flag —
   * `UserListScreen.tsx`'s role-catalogue effect can get away with a single
   * flag because only one fetch is ever in flight there; here `retry()` can
   * fire while the effect's own call is still in flight, and a THIRD filter
   * submission can land before either resolves, so "am I the most recent
   * call" needs a monotonic counter, not a single latch.
   *
   * Bumped at the top of every `load`, before the request is even sent, and
   * compared again immediately after `await` — in BOTH the resolve and the
   * reject path — before any `setState` that would show this call's result.
   * A call whose number no longer matches `requestSeq.current` lost the
   * race: some later call already started, and its own result — whichever
   * arrives — is what the screen must show instead.
   */
  const requestSeq = useRef(0);

  const load = useCallback(async (query: AuditQuery) => {
    const seq = ++requestSeq.current;
    setStatus('loading');
    setReference(null);

    try {
      const page = await api.auditLog(query);
      if (seq !== requestSeq.current) return; // superseded — drop this response
      setRows(page.data);
      // Read out of THIS response and nowhere else — see the file docblock's
      // note on the cursor never being parsed, inspected or constructed.
      // `AuditPage.meta` carries no `total` — see this component's Pagination
      // call below, which passes `total={null}` for the same reason.
      observe({ next_cursor: page.meta.next_cursor, total: null });
      setStatus(page.data.length === 0 ? (hasAppliedFilters(query) ? 'no-results' : 'empty') : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (seq !== requestSeq.current) return; // superseded — drop this response
      setReference(error.requestId ?? null);
      setStatus('error');
    }
    // `pages.observe` is stable (`useCursorPages`'s own `useCallback`).
  }, [observe]);

  useEffect(() => {
    void load(toQuery(appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }, [load, appliedFilters, pages.cursor, pages.perPage]);

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    // `pages.first()` resets the cursor stack; batched into the SAME commit
    // (React 18) as the state change below, so the effect above fires once
    // for this submission, not twice.
    pages.first();
    setAppliedFilters(draftFilters);
  }

  function handleClearFilters(): void {
    setDraftFilters(BLANK_FILTERS);
    pages.first();
    setAppliedFilters(BLANK_FILTERS);
  }

  function retry(): void {
    void load(toQuery(appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }

  function updateDraft(field: keyof FilterState) {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setDraftFilters((current) => ({ ...current, [field]: value }));
    };
  }

  // A URL, never a request — see `api.auditLogExportUrl`'s own docblock in
  // client.ts. Built from `appliedFilters`, never `cursor`: the export is
  // the whole filtered trail, not one page of it.
  const exportUrl = api.auditLogExportUrl(toQuery(appliedFilters));

  return (
    <div className={styles['page']}>
      {/*
        Export sits in the title row. Still a real `<a href>` — navigating
        to it is what makes the browser's own download machinery take the
        `text/csv` response (fetching it here would pull the whole export into
        memory as a string) — but drawn as the same secondary button every
        other screen's Export is, via `ButtonLink`.
      */}
      <PageActions>
        <ButtonLink href={exportUrl} icon={<DownloadSimpleIcon size={16} />}>
          {t('audit.exportLink')}
        </ButtonLink>
      </PageActions>

      {/* Decision V-7's notice is a standing fact about the list, so it lives
          in the filter card at a legible size rather than on the page ground. */}
      <FilterBar
        onSubmit={handleFilterSubmit}
        onClear={handleClearFilters}
        note={t('audit.notVerifiedNotice')}
      >
        <Input
          label={t('audit.filterActor')}
          width="compact"
          placeholder={t('audit.filterActorPlaceholder')}
          value={draftFilters.actor}
          onChange={updateDraft('actor')}
        />
        <Input
          label={t('audit.filterAction')}
          width="compact"
          placeholder={t('audit.filterActionPlaceholder')}
          value={draftFilters.action}
          onChange={updateDraft('action')}
        />
        <Input
          label={t('audit.filterEntityType')}
          width="compact"
          placeholder={t('audit.filterEntityTypePlaceholder')}
          value={draftFilters.entityType}
          onChange={updateDraft('entityType')}
        />
        <Input
          label={t('audit.filterFrom')}
          width="compact"
          type="date"
          value={draftFilters.from}
          onChange={updateDraft('from')}
        />
        <Input
          label={t('audit.filterTo')}
          width="compact"
          type="date"
          value={draftFilters.to}
          onChange={updateDraft('to')}
        />
      </FilterBar>

      <AuditEntryTable
        caption={t('audit.title')}
        rows={rows}
        status={status}
        error={{ title: t('audit.loadFailed') }}
        empty={{ body: t('audit.empty') }}
        noResults={{ title: t('audit.noResults') }}
        onClearFilters={handleClearFilters}
      />

      <Pagination pages={pages} total={null} rowsOnPage={rows.length} />

      {/*
        Deliberately OUTSIDE AuditEntryTable/DataTable's own alert box — see
        UserListScreen.tsx's identical note. A fixed region carries the
        support reference and its own retry, independent of whatever
        DataTable's internal error box does or does not offer.
      */}
      {status === 'error' ? (
        <div className={styles['recovery']}>
          <Button variant="secondary" onClick={retry} icon={<ArrowClockwiseIcon size={16} />}>
            {t('audit.retry')}
          </Button>

          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="auditLogRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}
